import React, { useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../../layouts/MainLayout";
import "../../../assets/styles/custom-styles.css";
import Form from "../../../components/forms/Form";
import useBaseService from "../../../hooks/services/useBaseService";
import useLoader from "../../../hooks/useLoader";
import useNotification from "../../../hooks/useNotification";
import useForm from "../../../hooks/useForm";
import { locationFields } from "../../../constants/forms/locationFields";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const EditOrganizationLocationPage = () => {
  const navigate = useNavigate();
  const { organizationId, addressId, locationId } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  const {
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    formErrors,
  } = useBaseService(navigate);
  const { formData, setFormData, handleChange } = useForm(
    setDefaultFieldValues(locationFields),
  );

  const {
    data: locationData,
    isLoading: isLoadingLocation,
    isError: isErrorLocation,
    error: locationQueryError,
  } = useQueryWithLoader({
    queryKey: [
      "organizationAddressLocation",
      organizationId,
      addressId,
      locationId,
    ],
    queryFn: async () => {
      showLoader();
      const response = await baseServiceGetByColumn(
        entities.organizations.organizationAddressLocation(
          organizationId,
          addressId,
          locationId,
        ),
      );
      return response.result;
    },
    enabled: !!organizationId && !!addressId && !!locationId,
  });

  useEffect(() => {
    if (!isLoadingLocation && locationData) {
      hideLoader();
      setFormData({
        location: {
          area: locationData.area,
          section: locationData.section,
          spot: locationData.spot,
          details: locationData.details,
        },
      });
    }
    if (isErrorLocation && locationQueryError) {
      hideLoader();
      console.error("Erro ao buscar localização:", locationQueryError);
      showNotification(
        "error",
        `Erro ao buscar localização: ${locationQueryError?.message}`,
      );
      navigate(
        `/aluguel/organizacoes/detalhes/${organizationId}/enderecos/${addressId}/localizacoes`,
      );
    }
  }, [locationData, isLoadingLocation, isErrorLocation, locationQueryError]);

  const updateLocation = useMutation({
    mutationFn: async (data) => {
      showLoader();
      const response = await baseServicePut(
        entities.organizations.organizationAddressLocationUpdate(
          organizationId,
          addressId,
          locationId,
        ),
        data,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        queryClient.invalidateQueries({
          queryKey: ["locationsOrganization", organizationId, addressId],
        });
        navigate(
          `/aluguel/organizacoes/detalhes/${organizationId}/enderecos/${addressId}/localizacoes`,
        );
      }
    },
    onError: (error) => {
      console.error("onError", error);
      hideLoader();
    },
  });

  const handleSubmit = async () => {
    try {
      const cleanedData = cleanPayload(formData);

      await updateLocation.mutateAsync(cleanedData);
    } catch (error) {
      console.error("Erro ao enviar localização:", {
        status: error.status,
        message: error.message,
      });
    }
  };

  const handleBack = () => {
    navigate(
      `/aluguel/organizacoes/detalhes/${organizationId}/enderecos/${addressId}/localizacoes`,
    );
  };

  return (
    <MainLayout selectedCompany="ALUCOM">
      <div className="container-fluid p-1">
        <PageHeader
          title="Editar Localização"
          showBackButton={true}
          backUrl={`/aluguel/organizacoes/detalhes/${organizationId}/enderecos/${addressId}/localizacoes`}
        />

        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Salvar"
          textLoadingSubmit="Salvando..."
          handleBack={handleBack}
        >
          {() => {
            const renderedSections = new Set();

            return locationFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={() => {}}
                    getSelectedValue={() => {}}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditOrganizationLocationPage;
